import React from 'react';
import { FormatterType } from '../../../Types/ClientModelTypes';
import EDGrid from '../../../BaseComponents/GridComponents/EDGrid';
import EDTypography from '../../../BaseComponents/Typography/EDTypography';
import { EDTextEdit } from '../../../BaseComponents/TextEdit/EDTextEdit';
import {
  IFormatter,
  ValueType,
} from '../../../Utils/Formatters/FormatterTypes';
import { findFormatter } from '../../../Utils/Formatters/FindFormatter';
import { withStyles } from 'tss-react/mui';
import EDStandardSelect from '../../../BaseComponents/Selects/StandardSelect/EDStandardSelect';

interface IState {
  formatterName: FormatterType;
  parsedResult: ValueType;
  parserParams: IParamsMap;
  displayerParams: IParamsMap;
  formatter?: IFormatter;
}

const styles = {
  root: {
    marginTop: 20,
  },
};

interface IProps {
  classes?: any;
}

interface IParamsMap {
  [key: string]: string;
}

const doneParsers = [
  'TEDFormatBool',
  'TEDFormatOrgNr',
  'TEDFormatCase',
  'TEDFormatGroupedNr',
  'TEDFormatInt',
  'TEDFormatFloat',
  'TEDFormatIntAsPercent',
  'TEDFormatIntAsDegrees',
  'TEDFormatInt64',
  'TEDFormatCurr',
];

const ResultDisplay = (props: {
  value: ValueType;
  displayFunc: (v: ValueType) => string;
}) => (
  <div>
    <EDTypography variant="caption">
      Parsed result: {String(props.value)}
    </EDTypography>
    <EDTypography variant="caption">
      Displayer result: {props.displayFunc(props.value)}
    </EDTypography>
  </div>
);

const Param = (props: {
  paramName: string;
  paramValue: string;
  onChange: (paramName: string, newParamValue: string) => void;
}) => (
  <EDTextEdit
    id={props.paramName}
    value={props.paramValue}
    label={props.paramName}
    onChange={(text: string) => props.onChange(props.paramName, text)}
  />
);

class ParserDemo extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      formatterName: '',
      parsedResult: '',
      parserParams: {},
      displayerParams: {},
    };
  }

  render() {
    return (
      <EDGrid
        container
        justifyContent="flex-start"
        alignItems="center"
        direction="column"
      >
        <EDGrid item xs={2} className={this.props.classes.root}>
          <EDTypography variant="h3">Parser demo</EDTypography>
          <EDStandardSelect
            label="Formatter"
            value={this.state.formatterName}
            items={[
              { header: 'None', value: '' },
              ...doneParsers.map(parserName => ({
                header: parserName,
                value: parserName,
              })),
            ]}
            onChange={this.handleFormatterChange}
          />
        </EDGrid>
        {this.state.formatterName !== '' && (
          <EDGrid
            item
            container
            xs={8}
            direction="row"
            alignItems="center"
            justifyContent="center"
          >
            <EDGrid item container direction="row" justifyContent="center">
              <EDGrid item xs={4}>
                <React.Fragment>
                  <EDTypography variant="h4">Parser params</EDTypography>
                  {Object.keys(this.state.parserParams).map(key => (
                    <Param
                      key={key}
                      onChange={this.onChangeParserParam}
                      paramName={key}
                      paramValue={this.state.parserParams[key]}
                    />
                  ))}
                </React.Fragment>
              </EDGrid>

              <EDGrid item xs={4}>
                <React.Fragment>
                  <EDTypography variant="h4">Displayer params</EDTypography>
                  {Object.keys(this.state.displayerParams).map(key => (
                    <Param
                      key={key}
                      onChange={this.onChangeDisplayerParams}
                      paramName={key}
                      paramValue={this.state.displayerParams[key]}
                    />
                  ))}
                </React.Fragment>
              </EDGrid>
            </EDGrid>

            <EDGrid item xs={6}>
              <EDTextEdit
                id="format-input"
                label="Input"
                onChange={() => {}}
                onBlur={this.handleInputChange}
              />
              <ResultDisplay
                displayFunc={this.displayResult}
                value={this.state.parsedResult}
              />
            </EDGrid>
          </EDGrid>
        )}
      </EDGrid>
    );
  }

  displayResult = (value: ValueType): string => {
    if (this.state.formatter) {
      return this.state.formatter.displayer.display(
        value,
        true,
        this.getDisplayerParams()
      );
    }

    return '';
  };

  onChangeParserParam = (paramName: string, paramValue: string) => {
    this.setState({
      parserParams: { ...this.state.parserParams, [paramName]: paramValue },
    });
  };

  onChangeDisplayerParams = (paramName: string, paramValue: string) => {
    this.setState({
      displayerParams: {
        ...this.state.displayerParams,
        [paramName]: paramValue,
      },
    });
  };

  handleFormatterChange = (newValue: string) => {
    const formatter = findFormatter(newValue);

    let parserParams: IParamsMap = {};
    let displayerParams: IParamsMap = {};

    formatter.parser
      .defaultParams()
      .split(';')
      .forEach(param => {
        if (!param) {
          return;
        }
        const paramName = param.split('=')[0];
        const paramValue = param.split('=')[1];

        parserParams[paramName] = paramValue.slice(1, paramValue.length - 1);
      });

    formatter.displayer
      .defaultParams()
      .split(';')
      .forEach(param => {
        if (!param) {
          return;
        }
        const paramName = param.split('=')[0];
        const paramValue = param.split('=')[1];

        displayerParams[paramName] = paramValue.slice(1, paramValue.length - 1);
      });

    this.setState({
      formatterName: newValue,
      formatter,
      parserParams,
      displayerParams,
    });
  };

  handleInputChange = (
    event: React.FocusEvent<HTMLDivElement | HTMLInputElement>
  ) => {
    const params = this.getParserParams();
    const value = (event.target as HTMLInputElement).value;

    const { value: parsedValue } = this.state.formatter
      ? this.state.formatter.parser.parse(value, params)
      : { value: 'Error' };
    this.setState({ parsedResult: parsedValue });
  };

  getParserParams = () => {
    const { parserParams } = this.state;

    return getParams(this.state, parserParams);
  };

  getDisplayerParams = () => {
    const { displayerParams } = this.state;

    return getParams(this.state, displayerParams);
  };
}

const getParams = (state: IState, params: IParamsMap) => {
  return Object.keys(params)
    .map((param, index) => {
      if (index === 0) {
        return `${param}="${params[param]}"`;
      } else {
        return `";${param}="${params[param]}"`;
      }
    })
    .join('');
};

export default withStyles(ParserDemo, styles);
